import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

interface Domain {
  id: string;
  name: string;
  color: string;
}

interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  rate_limit: number;
  is_whitelisted: number;
  allowed_ips: string[] | null;
  expires_at: string | null;
  is_active: number;
  created_at: string;
  last_used_at: string | null;
  total_requests: number;
  owner_username: string;
  owner_name: string;
  domains: Domain[];
}

interface ApiKeysProps {
  isSuperAdmin: boolean;
}

const ApiKeys: React.FC<ApiKeysProps> = ({ isSuperAdmin }) => {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [newKeyData, setNewKeyData] = useState<any>(null);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [selectedKey, setSelectedKey] = useState<ApiKey | null>(null);
  const [usageData, setUsageData] = useState<any>(null);

  const [formData, setFormData] = useState({
    name: '',
    rateLimit: 1000,
    isWhitelisted: false,
    allowedIps: '',
    expiresAt: '',
    domainIds: [] as string[]
  });

  useEffect(() => {
    loadKeys();
    loadDomains();
  }, []);

  const loadKeys = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/api-keys?includeInactive=true`);
      setKeys((response.data as any).keys || []);
    } catch (error) {
      console.error('Error loading API keys:', error);
      alert('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const loadDomains = async () => {
    try {
      const response = await axios.get(`${API_BASE}/domains`);
      setDomains((response.data as any).domains || []);
    } catch (error) {
      console.error('Error loading domains:', error);
    }
  };

  const createKey = async () => {
    if (!formData.name) {
      alert('Name is required');
      return;
    }

    try {
      setLoading(true);
      const payload: any = {
        name: formData.name,
        rateLimit: formData.rateLimit,
        isWhitelisted: formData.isWhitelisted,
        domainIds: formData.domainIds.length > 0 ? formData.domainIds : undefined
      };

      if (formData.allowedIps.trim()) {
        payload.allowedIps = formData.allowedIps.split(',').map(ip => ip.trim());
      }

      if (formData.expiresAt) {
        payload.expiresAt = formData.expiresAt;
      }

      const response = await axios.post(`${API_BASE}/api-keys`, payload);
      const data = response.data as any;

      // Store the new key to show in modal
      setNewKeyData(data);
      setShowCreateModal(false);
      setShowKeyModal(true);
      setFormData({
        name: '',
        rateLimit: 1000,
        isWhitelisted: false,
        allowedIps: '',
        expiresAt: '',
        domainIds: []
      });
      await loadKeys();
    } catch (error: any) {
      console.error('Error creating API key:', error);
      alert(error.response?.data?.error || 'Failed to create API key');
    } finally {
      setLoading(false);
    }
  };

  const revokeKey = async (id: string) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      await axios.delete(`${API_BASE}/api-keys/${id}`);
      await loadKeys();
      alert('API key revoked successfully');
    } catch (error: any) {
      console.error('Error revoking API key:', error);
      alert(error.response?.data?.error || 'Failed to revoke API key');
    } finally {
      setLoading(false);
    }
  };

  const viewUsage = async (key: ApiKey) => {
    try {
      setLoading(true);
      setSelectedKey(key);
      const response = await axios.get(`${API_BASE}/api-keys/${key.id}/usage?days=7`);
      setUsageData(response.data);
      setShowUsageModal(true);
    } catch (error) {
      console.error('Error loading usage:', error);
      alert('Failed to load usage data');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const toggleDomain = (domainId: string) => {
    if (formData.domainIds.includes(domainId)) {
      setFormData({
        ...formData,
        domainIds: formData.domainIds.filter(id => id !== domainId)
      });
    } else {
      setFormData({
        ...formData,
        domainIds: [...formData.domainIds, domainId]
      });
    }
  };

  return (
    <div className="api-keys-management">
      <div className="management-header">
        <h2>API Keys</h2>
        <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
          Generate New Key
        </button>
      </div>

      {loading && <div className="loading">Loading...</div>}

      <div className="api-keys-info">
        <p>
          API keys allow external systems to execute rules. Use the <code>X-API-Key</code> header
          or <code>api_key</code> query parameter to authenticate requests.
        </p>
      </div>

      {keys.length === 0 ? (
        <p className="empty-state">No API keys created yet</p>
      ) : (
        <table className="management-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Key Prefix</th>
              <th>Rate Limit</th>
              <th>Domains</th>
              <th>Status</th>
              <th>Created</th>
              <th>Last Used</th>
              <th>Requests</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {keys.map(key => (
              <tr key={key.id} className={key.is_active ? '' : 'inactive-row'}>
                <td>
                  <strong>{key.name}</strong>
                  {key.is_whitelisted === 1 && (
                    <span className="whitelist-badge" title="Bypasses rate limits">
                      Whitelisted
                    </span>
                  )}
                </td>
                <td>
                  <code>{key.key_prefix}...</code>
                </td>
                <td>
                  {key.is_whitelisted ? 'Unlimited' : `${key.rate_limit}/hr`}
                </td>
                <td>
                  {key.domains && key.domains.length > 0 ? (
                    <div className="key-domains">
                      {key.domains.map(d => (
                        <span
                          key={d.id}
                          className="domain-badge-small"
                          style={{ backgroundColor: d.color }}
                        >
                          {d.name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="all-domains">All domains</span>
                  )}
                </td>
                <td>
                  {key.is_active ? (
                    key.expires_at && new Date(key.expires_at) < new Date() ? (
                      <span className="status-expired">Expired</span>
                    ) : (
                      <span className="status-active">Active</span>
                    )
                  ) : (
                    <span className="status-revoked">Revoked</span>
                  )}
                </td>
                <td>{formatDate(key.created_at)}</td>
                <td>{formatDate(key.last_used_at)}</td>
                <td>{key.total_requests.toLocaleString()}</td>
                <td className="actions-cell">
                  <button
                    className="btn-small btn-secondary"
                    onClick={() => viewUsage(key)}
                  >
                    Usage
                  </button>
                  {key.is_active === 1 && (
                    <button
                      className="btn-small btn-danger"
                      onClick={() => revokeKey(key.id)}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Create Key Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Generate New API Key</h3>
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Production Server, Mobile App"
              />
            </div>
            <div className="form-group">
              <label>Rate Limit (requests/hour)</label>
              <input
                type="number"
                value={formData.rateLimit}
                onChange={(e) => setFormData({ ...formData, rateLimit: parseInt(e.target.value) || 1000 })}
                min={100}
                max={10000}
              />
              <small>Between 100 and 10,000 requests per hour</small>
            </div>
            {isSuperAdmin && (
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isWhitelisted}
                    onChange={(e) => setFormData({ ...formData, isWhitelisted: e.target.checked })}
                  />
                  <span>Whitelisted (bypass rate limits)</span>
                </label>
                <small>For trusted internal integrations only</small>
              </div>
            )}
            <div className="form-group">
              <label>Allowed IPs (optional)</label>
              <input
                type="text"
                value={formData.allowedIps}
                onChange={(e) => setFormData({ ...formData, allowedIps: e.target.value })}
                placeholder="e.g., 192.168.1.1, 10.0.0.0/8"
              />
              <small>Comma-separated. Leave empty to allow all IPs.</small>
            </div>
            <div className="form-group">
              <label>Expires At (optional)</label>
              <input
                type="datetime-local"
                value={formData.expiresAt}
                onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
              />
              <small>Leave empty for no expiration</small>
            </div>
            <div className="form-group">
              <label>Domain Restrictions (optional)</label>
              <div className="domain-checkboxes">
                {domains.map(domain => (
                  <label key={domain.id} className="domain-checkbox-item">
                    <input
                      type="checkbox"
                      checked={formData.domainIds.includes(domain.id)}
                      onChange={() => toggleDomain(domain.id)}
                    />
                    <span
                      className="domain-color-dot"
                      style={{ backgroundColor: domain.color }}
                    />
                    {domain.name}
                  </label>
                ))}
              </div>
              <small>Leave empty to allow access to all domains</small>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={createKey} disabled={loading}>
                {loading ? 'Generating...' : 'Generate Key'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Key Display Modal */}
      {showKeyModal && newKeyData && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>API Key Generated</h3>
            <div className="key-warning">
              <strong>Important:</strong> Copy this key now. You won't be able to see it again!
            </div>
            <div className="new-key-display">
              <code>{newKeyData.key.rawKey}</code>
              <button
                className="btn-secondary"
                onClick={() => copyToClipboard(newKeyData.key.rawKey)}
              >
                Copy
              </button>
            </div>
            <div className="key-details">
              <p><strong>Name:</strong> {newKeyData.key.name}</p>
              <p><strong>Rate Limit:</strong> {newKeyData.key.isWhitelisted ? 'Unlimited' : `${newKeyData.key.rateLimit}/hour`}</p>
              {newKeyData.key.domains && newKeyData.key.domains.length > 0 && (
                <p><strong>Domains:</strong> {newKeyData.key.domains.map((d: Domain) => d.name).join(', ')}</p>
              )}
            </div>
            <div className="usage-example">
              <h4>Usage Example:</h4>
              <pre>
{`curl -X POST \\
  -H "X-API-Key: ${newKeyData.key.rawKey}" \\
  -H "Content-Type: application/json" \\
  -d '{"data": {"creditScore": 750}}' \\
  ${window.location.origin}/api/execute/RULE_ID`}
              </pre>
            </div>
            <div className="modal-actions">
              <button className="btn-primary" onClick={() => setShowKeyModal(false)}>
                I've Copied the Key
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Usage Modal */}
      {showUsageModal && selectedKey && usageData && (
        <div className="modal-overlay">
          <div className="modal modal-large">
            <h3>API Key Usage: {selectedKey.name}</h3>
            <div className="usage-stats">
              <div className="stat-box">
                <span className="stat-value">{usageData.stats?.allTimeRequests?.toLocaleString() || 0}</span>
                <span className="stat-label">Total Requests</span>
              </div>
              <div className="stat-box">
                <span className="stat-value">{usageData.stats?.total_success?.toLocaleString() || 0}</span>
                <span className="stat-label">Successful</span>
              </div>
              <div className="stat-box">
                <span className="stat-value">{usageData.stats?.total_failures?.toLocaleString() || 0}</span>
                <span className="stat-label">Failed</span>
              </div>
              <div className="stat-box">
                <span className="stat-value">
                  {usageData.stats?.avg_response_ms ? `${Math.round(usageData.stats.avg_response_ms)}ms` : 'N/A'}
                </span>
                <span className="stat-label">Avg Response</span>
              </div>
            </div>
            {usageData.usage && usageData.usage.length > 0 ? (
              <table className="usage-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Hour</th>
                    <th>Requests</th>
                    <th>Success</th>
                    <th>Failed</th>
                    <th>Avg Response</th>
                  </tr>
                </thead>
                <tbody>
                  {usageData.usage.slice(0, 24).map((u: any, i: number) => (
                    <tr key={i}>
                      <td>{u.date}</td>
                      <td>{u.hour}:00</td>
                      <td>{u.request_count}</td>
                      <td>{u.success_count}</td>
                      <td>{u.failure_count}</td>
                      <td>{u.avg_response_ms ? `${Math.round(u.avg_response_ms)}ms` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="empty-state">No usage data available</p>
            )}
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowUsageModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiKeys;
